import React from 'react';
import { Drawer } from 'antd';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState, AppThunk } from 'src/store';
import { Routes } from 'src/constants/index';
import { CloseSvg, UserSvg } from 'src/assets/index';
import { getZendeskChat, isExternalUrl } from 'src/utils/index';
import {
  getNavigationMenu,
  IMenuState,
  setVisible,
  setIsUpdateMenu,
  getBenefits,
  IBenefitsState,
} from 'src/store/slices/index';
import { PointsLocationsEnum } from 'src/common-types/index';
import { NavigationMenu } from '../navigation';
import { WhiteButton } from '../../common';
import { SkeletonHeader } from '../skeleton-header';
import './style.scss';

interface IMapStateToProps {
  menuStore: IMenuState;
  benefitsStore: IBenefitsState;
}

interface IMapDispatchToProps {
  setVisible: (visible: boolean) => void;
  setIsUpdateMenu: (value: boolean) => void;
  getNavigationMenu: () => AppThunk;
  getBenefits: () => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {
  matches?: boolean;
}

interface IState {
  showDrawerUserInfo: boolean;
  isShowCredits: boolean;
}

const ZERO = 0;
const ONE = 1;

class HeaderComponent extends React.Component<IProps, IState> {
  state: IState = {
    showDrawerUserInfo: false,
    isShowCredits: false,
  };

  showDrawer = () => {
    this.props.setVisible(!this.props.menuStore.visible);
  };

  onClose = () => {
    this.props.setVisible(false);
  };

  showUserLinks = () => {
    this.setState({ showDrawerUserInfo: true });
  };

  onCloseUserLinks = () => {
    this.setState({ showDrawerUserInfo: false });
  };

  onCondoOrHotelPage = (): void => {
    if (this.props.menuStore?.items) {
      const { pointsSettings } = this.props.menuStore?.items;
      const isCondoPage =
        this.props.location.pathname.split('/')[ONE] === PointsLocationsEnum.Condo;
      const isCondoBookingPage =
        this.props.location.pathname.split('/')[ONE] === PointsLocationsEnum.CondoBooking;
      const isHotelPage =
        this.props.location.pathname.split('/')[ONE] === PointsLocationsEnum.Hotel;
      const isHotelBookingPage =
        this.props.location.pathname.split('/')[ONE] === PointsLocationsEnum.HotelBooking;
      const isCondo =
        (isCondoPage || isCondoBookingPage) && pointsSettings.condo.isPointsControlEnabled;
      const isHotel =
        (isHotelPage || isHotelBookingPage) && pointsSettings.hotel.isPointsControlEnabled;

      if (isCondo || isHotel) {
        this.setState({ isShowCredits: true });
      } else {
        this.setState({ isShowCredits: false });
      }
    }
  };

  componentDidMount() {
    this.props.getNavigationMenu();
    this.props.getBenefits();
  }

  componentDidUpdate(prevProps: Readonly<IProps>) {
    if (this.props.menuStore.isUpdateMenu) {
      this.props.getNavigationMenu();
      this.props.setIsUpdateMenu(false);
    }

    if (prevProps.menuStore?.items !== this.props.menuStore?.items) {
      getZendeskChat(this.props.menuStore.items.rsiId);
    }

    if (
      prevProps.menuStore.loading !== this.props.menuStore.loading &&
      !this.props.menuStore.loading
    ) {
      this.onCondoOrHotelPage();
    }

    if (prevProps.location !== this.props.location) {
      this.onCondoOrHotelPage();
    }
  }

  render(): React.ReactNode {
    const { matches, menuStore } = this.props;
    const { showDrawerUserInfo, isShowCredits } = this.state;
    const { visible, items, loading } = menuStore;

    return (
      <>
        {menuStore.error.length === ZERO && !loading ? (
          <div className="header print">
            <div className="header__info">
              <div className="header__info-item">
                <div className="header__info-call">
                  <p className="header__call-text">
                    <FormattedMessage id="call.gent.now" />
                  </p>
                  <a href={`tel:${items?.phone}`} className="header__call-number">
                    {items?.phone}
                  </a>
                </div>
                <a
                  href={items?.brochureUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="header__link-member"
                >
                  {matches ? (
                    <FormattedMessage id="member.brochure" />
                  ) : (
                    <FormattedMessage id="member.brochure.short" />
                  )}
                </a>
              </div>
              <div className="header__items">
                {items && items?.header
                  ? items.header.map((item, index) =>
                      isExternalUrl(item.url) ? (
                        <a
                          key={`${index}${item.title}`}
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          className="header__item-navigation"
                        >
                          {item.title}
                        </a>
                      ) : (
                        <Link
                          key={`${index}${item.title}`}
                          to={item.url}
                          className="header__item-navigation"
                        >
                          {item.title}
                        </Link>
                      ),
                    )
                  : null}
              </div>
            </div>
            <div className="header__content">
              {items ? (
                <Link to={Routes.Home} className="header__logo">
                  <img className="logo-icon" src={items?.logoUrl} alt={'logo'} />
                </Link>
              ) : null}
              <div className="header__content-navigation">
                <div
                  className={`header__hamburger-btn ${visible ? 'header__show-hamburger-btn' : ''}`}
                  onClick={this.showDrawer}
                >
                  <span className="header__hamburger-item" />
                  <span className="header__hamburger-item" />
                  <span className="header__hamburger-item" />
                </div>
                {matches && items?.main && (
                  <NavigationMenu matches={matches} menuStore={menuStore} />
                )}
                {!matches && items?.main && (
                  <Drawer
                    className="header__navigation"
                    width={'100%'}
                    closable={true}
                    onClose={this.onClose}
                    visible={visible}
                    closeIcon={<CloseSvg />}
                  >
                    <NavigationMenu matches={matches} menuStore={menuStore} />
                    <div className="header__modal-user-info">
                      <div className="header__modal-user-info-items">
                        {items && items?.header
                          ? items.header.map((item, index) => (
                              <a
                                key={`${index}${item.title}`}
                                href={item.url}
                                className="header__modal-item-navigation"
                              >
                                {item.title}
                              </a>
                            ))
                          : null}
                      </div>
                      <div className="header__modal-info-call">
                        <p className="header__modal-info-call-text">
                          <FormattedMessage id="call.gent.now" />
                        </p>
                        <a href={`tel:${items?.phone}`} className="header__modal-info-call-number">
                          {items?.phone}
                        </a>
                      </div>
                    </div>
                  </Drawer>
                )}
              </div>
              <div className="header__user-info">
                <p className="header__user-info-item">
                  {items?.firstName} {items?.lastName[0]}.
                </p>
                {!!isShowCredits && (
                  <p className="header__user-info-credits">{items?.leisureCredits}</p>
                )}
                <div onClick={this.showUserLinks} className="header__user-hamburger-btn">
                  {matches ? (
                    <>
                      <span className="header__user-hamburger-item" />
                      <span className="header__user-hamburger-item" />
                      <span className="header__user-hamburger-item" />
                    </>
                  ) : (
                    <UserSvg />
                  )}
                </div>
                <Drawer
                  className={`header__user-drawer ${!showDrawerUserInfo ? 'hide' : ''}`}
                  placement="right"
                  closable={true}
                  onClose={this.onCloseUserLinks}
                  visible={showDrawerUserInfo}
                  closeIcon={<CloseSvg />}
                >
                  <div className="header__user-drawer-wrapper">
                    <div className="header__user-drawer-info">
                      <p className="header__user-drawer-name">
                        {items?.firstName} {items?.lastName[0]}.
                      </p>
                      <p className="header__user-drawer-member">
                        <FormattedMessage id="member.ID" />
                        {': '}
                        {items?.rsiId}
                      </p>
                    </div>
                    {!!isShowCredits && (
                      <p className="header__user-drawer-credits">{items?.leisureCredits}</p>
                    )}
                  </div>
                  {items?.user ? (
                    <div className="header__user-drawer-links">
                      {items?.user.map((item, index) =>
                        isExternalUrl(item.url) ? (
                          <a
                            key={`${item.title}${index}`}
                            className="header__user-drawer-link"
                            href={item.url}
                            onClick={this.onCloseUserLinks}
                          >
                            {item.title}
                          </a>
                        ) : (
                          <Link
                            key={`${item.title}${index}`}
                            className="header__user-drawer-link"
                            to={item.url}
                            onClick={this.onCloseUserLinks}
                          >
                            {item.title}
                          </Link>
                        ),
                      )}
                    </div>
                  ) : null}
                  <a href={items?.logoutUrl} className="header__user-drawer-btn-wrapper">
                    <WhiteButton>
                      <FormattedMessage id="log.out" />
                    </WhiteButton>
                  </a>
                </Drawer>
              </div>
            </div>
          </div>
        ) : null}
        {loading ? <SkeletonHeader /> : null}
      </>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    menuStore: state.navigationMenuStore,
    benefitsStore: state.benefitsStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  getBenefits,
  setIsUpdateMenu,
  getNavigationMenu,
  setVisible,
};

export const Header = connect(mapStateToProps, mapDispatchToProps)(withRouter(HeaderComponent));

